import React, { Component, ReactElement } from 'react';

import { NotificationCenterAPI, NotificationData } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, bool, eventListener, object, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent, CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { isFunction, isMobile } from '@philips-emr/tws-utils';

import { Link, Modal, Popover } from '..';

import { NotificationItemRow } from './component';
import './notification-center.scss';

export interface NotificationCenterProps {
  data: NotificationData[];
  nativeOnMobile: boolean;
  onClick: (event: ClickEvent) => void;
  onClose: (event: CloseEvent) => void;
  title: string;
}

interface NotificationCenterState {
  open: boolean;
}

@AwesomeComponent({ type: ComponentType.DATA_DISPLAY, api: NotificationCenterAPI })
class NotificationCenter extends Component<NotificationCenterProps, NotificationCenterState> {
  state: NotificationCenterState = {
    open: false,
  };

  static propTypes = {
    data: arrayOf(object).notEnumerable(),
    nativeOnMobile: bool,
    onClick: eventListener,
    onClose: eventListener,
    title: string,
  };

  static defaultProps = {
    nativeOnMobile: true,
    title: 'tws.notification.title',
  };

  onClick = (): void => {
    this.setState({ open: true });
  };

  onItemClick = (item: NotificationData): void => {
    const { onClick } = this.props;

    if (isFunction(onClick)) {
      onClick(new ClickEvent({ value: item }));
    }
  };

  onClose = (): void => {
    const { onClose } = this.props;
    this.setState({ open: false }, () => {
      if (isFunction(onClose)) {
        onClose(new CloseEvent());
      }
    });
  };

  renderIcon(): ReactElement {
    const { data } = this.props;

    const itemCount = Array.isArray(data) ? data.filter((item) => !item.read).length : 0;

    return (
      <Link badge={String(itemCount)} className="c-notification__icon" onClick={this.onClick} value="" />
    );
  }

  renderPopover(): ReactElement {
    const { open } = this.state;

    return (
      <Popover onClose={this.onClose} open={open}>
        {this.renderIcon()}
        {this.renderPopoverContent()}
      </Popover>
    );
  }

  renderPopoverContent(): ReactElement {
    const { data, title } = this.props;

    return (
      <Popover.Content>
        <div className="c-notifications__popover">
          <div className="c-notifications__header">
            <div className="c-notifications__label">{msg(title)}</div>
          </div>
          <NotificationItemRow data={data} onItemClick={this.onItemClick} />
        </div>
      </Popover.Content>
    );
  }

  renderMobile(): ReactElement {
    const { data, title } = this.props;
    const { open } = this.state;

    return (
      <div>
        {this.renderIcon()}
        {open && (
          <Modal className="c-notifications__modal" title={title} onClose={this.onClose}>
            <NotificationItemRow data={data} onItemClick={this.onItemClick} />
          </Modal>
        )}
      </div>
    );
  }

  render(): ReactElement {
    const { nativeOnMobile } = this.props;

    return isMobile() && nativeOnMobile ? this.renderMobile() : this.renderPopover();
  }
}

export default NotificationCenter;
